/**
 * section-youtube-videos.js
 *
 * Handles:
 *  - Swiper carousel initialisation (reuses the globally-loaded swiper-bundle.min.js)
 *  - Thumbnail → iframe swap (play inline inside card)
 *  - Modal lightbox for a focused viewing experience
 *  - Keyboard accessibility (Esc to close, focus trap in modal)
 *  - Touch/swipe via Swiper's built-in support
 *  - No autoplay on load
 *  - YouTube's nocookie domain for reduced tracking
 */

'use strict';

/**
 * @param {string} swiperSel   CSS selector for the .swiper element
 * @param {string} prevSel     CSS selector for the prev button
 * @param {string} nextSel     CSS selector for the next button
 * @param {string} pagSel      CSS selector for the pagination container
 * @param {string} modalSel    CSS selector for the modal element
 * @param {Object} opts        Options from Liquid
 * @param {number} opts.desktopSlides
 * @param {number} opts.tabletSlides
 * @param {number} opts.mobileSlides
 * @param {number} opts.spaceBetween
 */
function initYouTubeSection(swiperSel, prevSel, nextSel, pagSel, modalSel, opts) {
  var swiperEl = document.querySelector(swiperSel);
  if (!swiperEl) return;

  var prevBtn   = document.querySelector(prevSel);
  var nextBtn   = document.querySelector(nextSel);
  var pagEl     = document.querySelector(pagSel);
  var modalEl   = document.querySelector(modalSel);

  var desktopSlides  = opts.desktopSlides || 3;
  var tabletSlides   = opts.tabletSlides  || 2;
  var mobileSlides   = opts.mobileSlides  || 1;
  var gap            = opts.spaceBetween  || 20;
  var progressBarEl  = opts.progressBarId ? document.getElementById(opts.progressBarId) : null;

  function updateProgress(instance) {
    if (!progressBarEl) return;
    var progress = Math.min(Math.max(instance.progress, 0), 1);
    var visibleRatio = instance.width / instance.virtualSize;
    var fill = instance.isLocked ? 100 : (visibleRatio + progress * (1 - visibleRatio)) * 100;
    requestAnimationFrame(function () {
      progressBarEl.style.width = fill + '%';
    });
  }

  /* ── 1. Init Swiper ────────────────────────────────────────────────── */
  var swiper = new Swiper(swiperSel, {
    slidesPerView: mobileSlides === 1 ? 1.08 : mobileSlides,
    spaceBetween: gap,
    grabCursor: true,
    watchSlidesProgress: true,
    a11y: {
      prevSlideMessage: 'Previous video',
      nextSlideMessage: 'Next video',
    },
    navigation: {
      nextEl: nextBtn,
      prevEl: prevBtn,
      disabledClass: 'swiper-button-disabled',
    },
    breakpoints: {
      750: {
        slidesPerView: tabletSlides,
        spaceBetween: gap,
      },
      990: {
        slidesPerView: desktopSlides,
        spaceBetween: gap,
      },
    },
    on: {
      init: function () {
        var self = this;
        setTimeout(function () { updateProgress(self); }, 150);
        if (document.readyState === 'complete') {
          setTimeout(function () { updateProgress(self); }, 300);
        } else {
          window.addEventListener('load', function () { updateProgress(self); }, { once: true });
        }
      },
      slideChange:    function () { updateProgress(this); },
      setTranslate:   function () { updateProgress(this); },
      resize:         function () { updateProgress(this); },
      /* Stop any playing inline videos when the user swipes away */
      slideChangeTransitionStart: function () {
        stopAllInlineVideos(swiperEl);
      },
    },
  });

  /* Run an initial progress update now that swiper is fully constructed */
  updateProgress(swiper);

  /* ── 2. Play button click → open modal ────────────────────────────── */
  swiperEl.addEventListener('click', function (e) {
    var playBtn = e.target.closest('.yt-card__play-btn');
    if (!playBtn) return;

    var mediaEl = playBtn.closest('.yt-card__media');
    if (!mediaEl) return;

    var videoId = mediaEl.dataset.videoId;
    var title   = mediaEl.dataset.videoTitle || 'YouTube video';
    if (!videoId) return;

    openModal(modalEl, videoId, title);
  });

  /* Allow keyboard activation of play buttons */
  swiperEl.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var playBtn = e.target.closest('.yt-card__play-btn');
    if (!playBtn) return;
    e.preventDefault();
    playBtn.click();
  });

  /* ── 3. Modal logic ───────────────────────────────────────────────── */
  if (!modalEl) return;

  var modalIframe  = modalEl.querySelector('.yt-modal__iframe');
  var modalClose   = modalEl.querySelector('.yt-modal__close');
  var modalBackdrop = modalEl.querySelector('.yt-modal__backdrop');
  var previousFocus = null;

  function openModal(modal, videoId, title) {
    var embedUrl = buildEmbedUrl(videoId, { autoplay: 1, rel: 0 });

    if (modalIframe) {
      modalIframe.src = embedUrl;
      modalIframe.title = title;
    }

    modal.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';

    previousFocus = document.activeElement;

    /* Focus the close button for keyboard users */
    if (modalClose) {
      setTimeout(function () { modalClose.focus(); }, 50);
    }

    /* Trap focus inside modal */
    modal.addEventListener('keydown', trapFocusHandler);

    /* Close on Esc */
    document.addEventListener('keydown', escHandler);
  }

  function closeModal(modal) {
    if (modalIframe) modalIframe.src = '';
    modal.setAttribute('hidden', '');
    document.body.style.overflow = '';

    modal.removeEventListener('keydown', trapFocusHandler);
    document.removeEventListener('keydown', escHandler);

    if (previousFocus && typeof previousFocus.focus === 'function') {
      previousFocus.focus();
    }
    previousFocus = null;
  }

  function escHandler(e) {
    if (e.key === 'Escape') closeModal(modalEl);
  }

  function trapFocusHandler(e) {
    if (e.key !== 'Tab') return;
    var focusable = Array.from(
      modalEl.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(function (el) { return !el.hasAttribute('disabled'); });

    if (!focusable.length) return;

    var first = focusable[0];
    var last  = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  if (modalClose) {
    modalClose.addEventListener('click', function () { closeModal(modalEl); });
  }

  if (modalBackdrop) {
    modalBackdrop.addEventListener('click', function () { closeModal(modalEl); });
  }

  /* ── 4. Helpers ───────────────────────────────────────────────────── */
  function buildEmbedUrl(videoId, params) {
    var base = 'https://www.youtube-nocookie.com/embed/' + videoId;
    var qs = Object.keys(params)
      .map(function (k) { return k + '=' + params[k]; })
      .join('&');
    return base + '?' + qs;
  }

  function stopAllInlineVideos(container) {
    container.querySelectorAll('.yt-card__media.is-playing').forEach(function (media) {
      var wrap   = media.querySelector('.yt-card__iframe-wrap');
      var iframe = wrap && wrap.querySelector('iframe');
      if (iframe) iframe.src = '';
      if (wrap) wrap.classList.remove('is-active');
      media.classList.remove('is-playing');
    });
  }
}
